import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { SecureStorageService } from '@services/secure-storage.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-payment-approved',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-approved.component.html',
  styleUrls: ['../../../../auth.scss'],
})
export class PaymentApprovedComponent implements OnInit, AfterViewInit {

  @ViewChild('confetti') confettiContainer!: ElementRef;
  userId: string | null = null;
  sessionId: string | null = null;
  authService = inject(AuthService);
  secureStorage = inject(SecureStorageService);
  isLoggedIn = false;
  isPaymentVerified = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private pageService: PagesService,
    private sharedService: SharedService
  ) {
    this.updateLoginStatus();
  }

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    this.userId =
      urlParams.get('user_id') ||
      urlParams.get('userId') ||
      this.route.snapshot.paramMap.get('user_id');
    this.sessionId = urlParams.get('session_id') || urlParams.get('sessionId') || this.route.snapshot.queryParamMap.get('session_id');
    this.updateLoginStatus();
  }

  private updateLoginStatus(): void {
    const hasToken = this.authService.isLoggedIn();
    const hasUserDetails = !!this.authService.getUserDetails();
    this.isLoggedIn = hasToken || hasUserDetails;
  }

  ngAfterViewInit(): void {
    this.checkPayment(this.userId, this.sessionId);
  }

  goToHomepage() {
    if (this.isLoggedIn) {
      this.router.navigate(['/home']);
    } else {
      this.router.navigate(['/auth/login']);
    }
    this.sharedService.hideLoader();
  }

  goToContests() {
    if (this.isLoggedIn) {
      this.router.navigate(['/home/contests']);
    } else {
      this.router.navigate(['/auth/login']);
    }
    this.sharedService.hideLoader();
  }

  goToInvoice() {
    if (!this.isLoggedIn) {
      this.router.navigate(['/auth/login']);
      this.sharedService.hideLoader();
      return;
    }

    this.router.navigate(['/home/billing'], {
      queryParams: {
        tab: 'invoice',
        payment_success: 'true'
      }
    });
    this.sharedService.hideLoader();
  }

  initializeConfetti() {
    setTimeout(() => {
      const container = this.confettiContainer?.nativeElement;

      if (!container) return;

      const totalPieces = 500;

      for (let i = 0; i < totalPieces; i++) {
        const confetti = document.createElement('div');
        confetti.classList.add('confetti-piece');

        confetti.style.left = Math.random() * 100 + 'vw';
        confetti.style.top = Math.random() * 10 + 'px';
        confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 70%, 60%)`;

        const size = Math.random() * 8 + 4;
        confetti.style.width = `${size}px`;
        confetti.style.height = `${size}px`;

        confetti.style.animationDelay = Math.random() * 2 + 's';
        confetti.style.animationDuration = (Math.random() * 2 + 3) + 's';

        confetti.addEventListener('animationend', () => {
          confetti.remove();
        });

        container.appendChild(confetti);
      }
    }, 100);
  }

  checkPayment(user_id: string | null, session_id: string | null) {
    if (!session_id) {
      this.isPaymentVerified = true;
      this.initializeConfetti();
      return;
    }

    this.sharedService.showLoader();
    const user = this.authService.getUserDetails();
    const effectiveUserId = user_id ? Number(user_id) : (user?.id ? Number(user.id) : 0);

    const payload = {
      user_id: effectiveUserId,
      session_id: session_id || ''
    };

    const req$ = effectiveUserId
      ? this.pageService.update('CHECK_PAYMENT_BY_USER', payload)
      : this.pageService.update('CHECK_PAYMENT', { session_id: session_id });

    req$.subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        const isSuccess = response?.data?.isSuccess || response?.isSuccess || response?.status === 'paid' || response?.data?.status === 'paid';
        if (isSuccess || !response?.error) {
          this.isPaymentVerified = true;
          this.initializeConfetti();

          // Strip session_id from the URL once verified so a reload or browser
          // Back doesn't resend the same session_id and re-trigger this success screen.
          window.history.replaceState(null, '', window.location.pathname);

          localStorage.removeItem('sub_id');
          localStorage.removeItem('sub_plan_id');

          const userDetails = this.secureStorage.getItem<any>('userDetails');
          if (userDetails) {
            userDetails.premimum_user = true;
            void this.secureStorage.setItem('userDetails', userDetails);
          }

          this.pageService.getAll('PURCHASED_PLAN').subscribe({
            next: (planResponse: any) => {
              const hasSdkAccess = planResponse?.sdk_features === true;
              const hasAiAccess = planResponse?.ai_attributes === true;
              const hasWebAccess = (planResponse?.sub_plan?.web_access ?? planResponse?.web_access) === true;
              void this.secureStorage.setItem('hasSdkAccess', hasSdkAccess);
              void this.secureStorage.setItem('hasAiAccess', hasAiAccess);
              void this.secureStorage.setItem('hasWebAccess', hasWebAccess);
              this.sharedService.updatedUserData.next(planResponse);
            },
            error: () => {
              this.sharedService.updatedUserData.next(true);
            }
          });
        } else {
          this.sharedService.createNotification('error', 'Issue in payment');
        }
      },
      error: (err) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', err?.message || err || 'Issue in payment');
      }
    });
  }
}
